/**
 * ServiceManager - Менеджер для управління сервісами додатку
 *
 * Надає централізоване управління сервісами з типізацією,
 * ледачою ініціалізацією та залежностями
 */

import { action, makeObservable } from 'mobx'
import { loggerManager } from '../Logger'

export interface ServiceFactory<T = unknown> {
    (): T
}

export interface ServiceDefinition<T = unknown> {
    factory: ServiceFactory<T>
    singleton?: boolean
    dependencies?: string[]
}

export interface ServiceManagerOptions {
    debug?: boolean
    context?: Record<string, unknown>
}

export class ServiceManager {
    private _services = new Map<string, unknown>()
    private _definitions = new Map<string, ServiceDefinition>()
    private _instances = new Map<string, unknown>()
    private debug: boolean
    private context: Record<string, unknown>

    constructor(options: ServiceManagerOptions = {}) {
        this.debug = options.debug ?? false
        this.context = options.context ?? {}

        makeObservable(this, {
            sets: action,
            set: action,
            register: action,
            clear: action,
            clearInstances: action,
        })
    }

    /**
     * Встановлює множинні сервіси з об'єкта
     */
    sets(services: Record<string, unknown>): void {
        if (typeof services !== 'object' || services === null) {
            if (this.debug) {
                loggerManager.warn('[ServiceManager] sets: Передані некоректні дані', 'service-manager', { services })
            }
            return
        }

        for (const key in services) {
            if (services.hasOwnProperty(key)) {
                this._services.set(key, services[key])
            }
        }
    }

    /**
     * Встановлює один сервіс
     */
    set(key: string, service: unknown): void {
        this._services.set(key, service)
    }

    /**
     * Реєструє сервіс з фабрикою для ледачої ініціалізації
     */
    register<T = unknown>(
        key: string,
        factory: ServiceFactory<T>,
        options: { singleton?: boolean; dependencies?: string[] } = {}
    ): void {
        const definition: ServiceDefinition<T> = {
            factory,
            singleton: options.singleton ?? true,
            dependencies: options.dependencies ?? [],
        }

        this._definitions.set(key, definition)
    }

    /**
     * Отримує сервіс за ключем
     */
    get<T = unknown>(key: string, defaultValue?: T): T | undefined {
        // Спочатку перевіряємо звичайні сервіси
        if (this._services.has(key)) {
            return this._services.get(key) as T | undefined
        }

        // Потім перевіряємо зареєстровані сервіси
        if (this._definitions.has(key)) {
            return this._createService<T>(key)
        }

        if (this.debug && defaultValue === undefined) {
            loggerManager.warn(`[ServiceManager] Сервіс не знайдено: ${key}`, 'service-manager')
        }

        return defaultValue
    }

    /**
     * Створює або отримує екземпляр сервісу
     */
    private _createService<T>(key: string): T {
        const definition = this._definitions.get(key)
        if (!definition) {
            throw new Error(`Визначення сервісу не знайдено: ${key}`)
        }

        // Якщо синглтон і вже створено - повертаємо існуючий
        if (definition.singleton && this._instances.has(key)) {
            return this._instances.get(key) as T
        }

        // Перевіряємо залежності
        const dependencies: unknown[] = []
        if (definition.dependencies && definition.dependencies.length > 0) {
            for (const depKey of definition.dependencies) {
                const dependency = this.get(depKey)
                if (dependency === undefined) {
                    throw new Error(`Залежність не знайдено: ${depKey} для сервісу ${key}`)
                }
                dependencies.push(dependency)
            }
        }

        // Створюємо екземпляр
        const instance = definition.factory()

        // Зберігаємо для синглтонів
        if (definition.singleton) {
            this._instances.set(key, instance)
        }

        return instance as T
    }

    /**
     * Отримує всі сервіси у вигляді об'єкта
     */
    getAll(): Record<string, unknown> {
        const result: Record<string, unknown> = {}

        // Додаємо звичайні сервіси
        this._services.forEach((service: unknown, key: string) => {
            result[key] = service
        })

        // Додаємо зареєстровані сервіси (тільки створені екземпляри)
        this._instances.forEach((instance: unknown, key: string) => {
            result[key] = instance
        })

        return result
    }

    /**
     * Перевіряє наявність сервісу
     */
    has(key: string): boolean {
        return this._services.has(key) || this._definitions.has(key)
    }

    /**
     * Видаляє сервіс
     */
    delete(key: string): boolean {
        const hasService = this._services.delete(key)
        const hasDefinition = this._definitions.delete(key)
        const hasInstance = this._instances.delete(key)

        const result = hasService || hasDefinition || hasInstance

        return result
    }

    /**
     * Очищає всі сервіси
     */
    clear(): void {
        this._services.clear()
        this._definitions.clear()
        this._instances.clear()
    }

    /**
     * Очищає тільки створені екземпляри (залишає визначення)
     */
    clearInstances(): void {
        this._instances.clear()
    }

    /**
     * Отримує кількість сервісів
     */
    get size(): number {
        return this._services.size + this._definitions.size
    }

    /**
     * Отримує всі ключі сервісів
     */
    get keys(): string[] {
        const keys = new Set<string>()
        this._services.forEach((_, key) => keys.add(key))
        this._definitions.forEach((_, key) => keys.add(key))
        return Array.from(keys)
    }

    /**
     * Отримує кількість створених екземплярів
     */
    get instancesCount(): number {
        return this._instances.size
    }

    /**
     * Отримує інформацію про зареєстровані сервіси
     */
    getRegisteredServices(): Record<string, { singleton: boolean; dependencies: string[] }> {
        const result: Record<string, { singleton: boolean; dependencies: string[] }> = {}

        this._definitions.forEach((definition, key) => {
            result[key] = {
                singleton: definition.singleton ?? true,
                dependencies: definition.dependencies ?? [],
            }
        })

        return result
    }

    /**
     * Перевіряє, чи порожній менеджер
     */
    get isEmpty(): boolean {
        return this._services.size === 0 && this._definitions.size === 0
    }

    /**
     * Повертає JSON представлення всіх сервісів
     */
    toJSON(): Record<string, unknown> {
        return this.getAll()
    }

    /**
     * Виводить інформацію про стан менеджера
     */
    debug_info(): void {
        loggerManager.debug('[ServiceManager] Debug Info', 'service-manager', {
            size: this.size,
            instancesCount: this.instancesCount,
            context: this.context,
            services: this.keys,
            registered: this.getRegisteredServices()
        })
    }
}

/**
 * Фабрична функція для створення екземпляра ServiceManager
 */
export function createServiceManager(options?: ServiceManagerOptions): ServiceManager {
    return new ServiceManager(options)
}

/**
 * Глобальний екземпляр ServiceManager для загального використання
 */
export const serviceManager = createServiceManager({
    debug: true, // Вмикаємо дебаг у розробці
})

export default ServiceManager
